/*
 * Copyright (c) 2019. Sonrai Analytics Ltd.
 * Developed by Ryan Duffy
 */

import { Injectable } from '@angular/core';
import { OrganisationUsersModel } from '@app/models/organisation-users.model';
import { ApiBaseService } from '@app/services/api-services/api-base.service';
import { HttpClient } from '@angular/common/http';
import { AuthUserService } from '@app/services/authentication/auth-user-service';
@Injectable({
  providedIn: 'root',
})
export class OrganisationUsersListService extends ApiBaseService {
  private _organisationUsersList: OrganisationUsersModel[] = [];
  protected _endpoint = 'organisation_users';

  constructor(http: HttpClient, authUserService: AuthUserService) {
    super(http, authUserService);
  }

  get organisationUsersList(): OrganisationUsersModel[] {
    return this._organisationUsersList;
  }

  set organisationUsersList(value: OrganisationUsersModel[]) {
    this._organisationUsersList = value;
  }

  /**
   *  Warren Dowey & Ryan Duffy
   *
   *  getOrganisationByOrganisationId
   *
   * : Get an organisation based on the id by checking the list
   *
   * @param id of type string
   *
   * @returns Void: gets the organisation with the id that meets the query
   */
  getOrganisationUserByOrganisationId(organisationId: string): OrganisationUsersModel {
    for (const organisation of this.organisationUsersList) {
      if (organisation.organisationId === organisationId) {
        return organisation;
      }
    }
    return null;
  }

  /**
   *  Warren Dowey & Ryan Duffy
   *
   *  getOrganisationByOrganisationId
   *
   * : Get an organisation based on the id by checking the list
   *
   * @param id of type string
   *
   * @returns Void: gets the organisation with the id that meets the query
   */
  getOrganisationUsersByOrganisationId(organisationId: string): OrganisationUsersModel[] {
    const list = [];
    for (const organisation of this.organisationUsersList) {
      if (organisation.organisationId === organisationId) {
        list.push(organisation);
      }
    }
    return list;
  }

  /**
   *
   * : Get an organisation user based on the id by checking the list
   * @returns Void: gets the user with the id that meets the query
   * @param userId
   */
  getOrganisationUserByUserId(userId: string): OrganisationUsersModel {
    for (const organisation of this.organisationUsersList) {
      if (organisation.userId === userId) {
        return organisation;
      }
    }
    return null;
  }

  /**
   *   Warren Dowey & Ryan Duffy
   *
   *  addtoList
   *
   * : handles the insert of a new organisationUser into the list
   *
   * @param data of type OrganisationUsersModel
   *
   * @returns Void: inserts a new organisationUser into the lsit
   */
  addToList(data: OrganisationUsersModel): void {
    this.organisationUsersList.push(data);
  }
  /**
   *   Warren Dowey & Ryan Duffy
   *
   *  removeFromList
   *
   * : deletes an organisationUser from the list
   *
   * @param data of type OrganisationUsersModel
   *
   * @returns Void: deletes a organisationUser
   */
  removeFromList(data: OrganisationUsersModel): void {
    let index = 0;
    for (const organisationUser of this.organisationUsersList) {
      if (
        organisationUser.organisationId === data.organisationId &&
        data.userId === organisationUser.userId
      ) {
        this.organisationUsersList.splice(index, 1);
      }
      index++;
    }
  }
  /**
   *  Warren Dowey
   *
   *  updateInList
   *
   * : handles the update of an organisationUser in the list
   *
   * @param data of type organisationUsermodel
   *
   * @returns Void: update the list with the data
   */
  updateInList(data: OrganisationUsersModel): void {
    let index = 0;
    for (let organisationUser of this.organisationUsersList) {
      if (organisationUser.organisationId === data.organisationId) {
        this.organisationUsersList.splice(index, 1);
        this.organisationUsersList.push(data);
      }
      index++;
    }
  }
  /**
   *  Warren Dowey
   *
   *  handleData
   *
   * : handles the data from the list by extending the handleData function from the API base service
   *
   * @param data of type OrganisationUsersModel
   *
   * @returns Void: get the organisationUser from the postgres database and create the list
   */
  protected handleData(data: any): void {
    this.fetchTimestamp = new Date();
    this.organisationUsersList = [];
    super.handleData(data);
    for (const item of data) {
      this.addToList(new OrganisationUsersModel().fromJson(item));
    }
  }
}
